import { useEffect, useMemo, useState } from "react";
import type { ContractTenor, FuturesHistoryCurve, FuturesHistorySeries } from "../lib/api";
import { ChartCard } from "./ChartCard";
import { ChartLegend, type LegendGroup } from "./ChartLegend";
import { LineChart } from "./LineChart";
import type { Series } from "../lib/chart";
import { DateRangeControl, RANGE_OPTIONS, resolveRangeMs } from "./DateRangeControl";
import { RangeSlider } from "./RangeSlider";
import {
  loadFuturesChartState,
  saveFuturesChartState,
  type DateRangeId,
  type FuturesChartState,
} from "../lib/storage";

interface Props {
  curve: FuturesHistoryCurve;
  updatedAt: string | null;
}

/** Legend grouping order + German headings. */
const TENOR_GROUPS: ReadonlyArray<{ key: ContractTenor; label: string }> = [
  { key: "month", label: "Monate" },
  { key: "quarter", label: "Quartale" },
  { key: "year", label: "Jahre" },
];

/**
 * One futures-history card: line chart over ~5 years of daily settlements for
 * the *real* contracts of a single market — actual months (Jul-2026),
 * quarters (Q3-2026) and calendar years (Cal-2027), each tracking one
 * physical product over its tradeable life (the worker regroups the rolling
 * front data by ContractName).
 *
 * Behaviour:
 *   - Only the year (Cal-*) contracts are visible by default; months and
 *     quarters are toggleable via the grouped legend.
 *   - The legend is window-aware: it lists exactly the contracts that have a
 *     settlement inside the currently selected window (range + zoom), so
 *     products that weren't trading in that period disappear entirely.
 *   - The worker ships ~5 years in one payload (TanStack Query holds it). All
 *     range/zoom changes slice client-side — never refetch.
 *   - Toggle visibility + range + zoom persist per curve under
 *     `montel-dashboard:futures-chart-state-v2`.
 */
export function FuturesCurveChart({ curve, updatedAt }: Props) {
  // Stable colour per contract id: a hue family per tenor (months / quarters /
  // years), shaded chronologically so neighbouring contracts stay tellable
  // apart. Toggling never reshuffles since the map is keyed by id.
  const colorById = useMemo(() => {
    const map: Record<string, string> = {};
    const byTenor: Record<ContractTenor, FuturesHistorySeries[]> = {
      month: [],
      quarter: [],
      year: [],
    };
    for (const s of curve.series) byTenor[s.tenor].push(s);
    (Object.keys(byTenor) as ContractTenor[]).forEach((tenor) => {
      const arr = byTenor[tenor].slice().sort((a, b) => a.deliveryStartMs - b.deliveryStartMs);
      arr.forEach((s, i) => {
        map[s.id] = colorForTenor(tenor, i, arr.length);
      });
    });
    return map;
  }, [curve.series]);

  // Initial chart state per curve: load persisted overrides, otherwise fall
  // back to "year contracts visible via sticky mode, 1Y range, no zoom".
  // `stickyTenors: ["year"]` means: any Cal-* contract is visible by
  // default, including new Cal-YYYY entries that appear at year-end
  // rollover — same principle as the fix for the months / quarters bug.
  const defaultState = useMemo<FuturesChartState>(
    () => ({
      visible: [],
      stickyTenors: ["year"],
      range: "1y",
      zoom: null,
    }),
    [],
  );
  const [state, setState] = useState<FuturesChartState>(defaultState);

  useEffect(() => {
    const loaded = loadFuturesChartState(curve.id, defaultState);
    // Migrate v2 state (which lacks `stickyTenors`): any tenor whose
    // contracts appear in `visible` is treated as sticky, so a user who
    // had "all months" or "all quarters" turned on before the rollover
    // fix still sees them all — plus the newly-tradable ones — after
    // this deploy. New defaults ({year} sticky) apply when the load
    // yields a completely fresh state.
    if (loaded.stickyTenors === undefined) {
      const derived = new Set<ContractTenor>();
      for (const s of curve.series) {
        if (loaded.visible.includes(s.id)) derived.add(s.tenor);
      }
      loaded.stickyTenors = [...derived];
    }
    setState(loaded);
  }, [curve.id, curve.series, defaultState]);

  // Persist on every change.
  useEffect(() => {
    saveFuturesChartState(curve.id, state);
  }, [curve.id, state]);

  // A contract is visible iff its tenor is sticky OR its id is explicitly
  // enabled. Sticky mode is what makes newly-tradable contracts (e.g. the
  // Jan-2027 M4 that appears after Sep-2026 rolls off) show up without a
  // manual click.
  const stickySet = useMemo(
    () => new Set<ContractTenor>(state.stickyTenors ?? []),
    [state.stickyTenors],
  );
  const explicitSet = useMemo(() => new Set(state.visible), [state.visible]);
  const isVisibleFor = (s: FuturesHistorySeries) =>
    stickySet.has(s.tenor) || explicitSet.has(s.id);

  // Click on a single contract's legend entry:
  //   - If the contract's tenor is currently sticky, "promote" the group
  //     to explicit mode: seed `visible` with every in-window id of that
  //     tenor, then flip the clicked one. Without this step, clicking off
  //     a single Q3-2026 in sticky mode wouldn't hide it (the tenor is
  //     still sticky), and the user would see nothing change.
  //   - Otherwise, plain toggle in the explicit list.
  const toggle = (id: string) =>
    setState((prev) => {
      const clicked = curve.series.find((s) => s.id === id);
      if (!clicked) return prev;
      const sticky = new Set<ContractTenor>(prev.stickyTenors ?? []);
      if (sticky.has(clicked.tenor)) {
        const inGroup = inWindowSeries.filter((s) => s.tenor === clicked.tenor);
        const next = new Set(prev.visible);
        for (const s of inGroup) next.add(s.id);
        next.delete(id);
        sticky.delete(clicked.tenor);
        return { ...prev, visible: [...next], stickyTenors: [...sticky] };
      }
      const next = new Set(prev.visible);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, visible: [...next] };
    });

  // Compute the dataset's calendar span once — we need it for the slider
  // extents and for resolving "All".
  const { dataStartMs, dataEndMs } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const s of curve.series) {
      for (const p of s.points) {
        if (p.settlement === null) continue;
        const t = Date.parse(p.date);
        if (!Number.isFinite(t)) continue;
        if (t < lo) lo = t;
        if (t > hi) hi = t;
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      const now = Date.now();
      return { dataStartMs: now - 365 * 24 * 3600 * 1000, dataEndMs: now };
    }
    return { dataStartMs: lo, dataEndMs: hi };
  }, [curve.series]);

  // Resolve the effective window from (range + zoom).
  const window = useMemo(() => {
    const base = resolveRangeMs(state.range, dataStartMs, dataEndMs);
    if (!state.zoom) return base;
    const span = base.end - base.start;
    return {
      start: base.start + state.zoom.start * span,
      end: base.start + state.zoom.end * span,
    };
  }, [state.range, state.zoom, dataStartMs, dataEndMs]);

  // Contracts that actually traded inside the current window — the legend and
  // the chart are both driven off this set, so anything not trading in the
  // selected period drops out completely.
  const inWindowSeries = useMemo(
    () => curve.series.filter((s) => hasPointsInWindow(s, window.start, window.end)),
    [curve.series, window.start, window.end],
  );

  // Filter points to the visible window.
  const renderedSeries: Series[] = useMemo(() => {
    return inWindowSeries
      .filter(isVisibleFor)
      .map((s) => {
        const sliced: Array<{ x: string; y: number | null }> = [];
        for (const p of s.points) {
          const t = Date.parse(p.date);
          if (!Number.isFinite(t)) continue;
          if (t < window.start || t > window.end) continue;
          sliced.push({ x: p.date, y: p.settlement });
        }
        return {
          id: s.id,
          label: `${curve.marketPrefix} ${s.contractName}`,
          color: colorById[s.id],
          points: sliced,
        };
      });
  }, [inWindowSeries, curve.marketPrefix, stickySet, explicitSet, colorById, window.start, window.end]);

  // Build the grouped legend (Monate / Quartale / Jahre) from the in-window
  // contracts only. Empty groups are omitted.
  const legendGroups: LegendGroup[] = useMemo(() => {
    return TENOR_GROUPS.flatMap(({ key, label }) => {
      const entries = inWindowSeries
        .filter((s) => s.tenor === key)
        .map((s) => ({
          id: s.id,
          label: s.contractName,
          color: colorById[s.id],
          visible: isVisibleFor(s),
        }));
      if (entries.length === 0) return [];
      return [
        {
          key,
          label,
          entries,
          allVisible: entries.every((e) => e.visible),
          anyVisible: entries.some((e) => e.visible),
        },
      ];
    });
  }, [inWindowSeries, colorById, stickySet, explicitSet]);

  // Group toggle: enter/leave sticky mode for that tenor. Sticky mode is
  // what makes new post-rollover contracts auto-appear — flipping it via
  // this button is the primary way users express "show me every month
  // (including any that become tradeable later)".
  const toggleGroup = (key: string) =>
    setState((prev) => {
      const tenor = key as ContractTenor;
      const sticky = new Set<ContractTenor>(prev.stickyTenors ?? []);
      const explicit = new Set(prev.visible);
      const idsInGroup = curve.series.filter((s) => s.tenor === tenor).map((s) => s.id);
      const anyOn = sticky.has(tenor) || idsInGroup.some((id) => explicit.has(id));
      if (anyOn) {
        // Turn the whole group off: leave sticky, drop every explicit id.
        sticky.delete(tenor);
        for (const id of idsInGroup) explicit.delete(id);
      } else {
        // Turn the whole group on: enter sticky mode; explicit ids of
        // this tenor become redundant but harmless.
        sticky.add(tenor);
      }
      return { ...prev, stickyTenors: [...sticky], visible: [...explicit] };
    });

  // Range-button click: switch range, clear any zoom override.
  const setRange = (id: DateRangeId) =>
    setState((prev) => ({ ...prev, range: id, zoom: null }));

  // Slider drag → translate absolute ms into a fraction of the *button*
  // window so it survives data refreshes (data shifts by 1 day each day).
  const sliderBase = useMemo(
    () => resolveRangeMs(state.range, dataStartMs, dataEndMs),
    [state.range, dataStartMs, dataEndMs],
  );
  const sliderValue = window;
  const onSliderChange = (next: { start: number; end: number }) => {
    const span = sliderBase.end - sliderBase.start;
    if (span <= 0) return;
    setState((prev) => ({
      ...prev,
      zoom: {
        start: Math.max(0, (next.start - sliderBase.start) / span),
        end: Math.min(1, (next.end - sliderBase.start) / span),
      },
    }));
  };

  return (
    <ChartCard
      title={curve.title}
      unit="EUR/MWh"
      accent={curve.accent}
      updatedAt={updatedAt}
      controls={<DateRangeControl active={state.range} onChange={setRange} />}
      footer={<ChartLegend groups={legendGroups} onToggle={toggle} onToggleGroup={toggleGroup} />}
    >
      <LineChart series={renderedSeries} unit="€/MWh" height={260} />
      <div className="mt-2">
        <RangeSlider
          min={sliderBase.start}
          max={sliderBase.end}
          value={sliderValue}
          onChange={onSliderChange}
          formatTick={fmtTickDate}
        />
      </div>
    </ChartCard>
  );
}

/** A contract has a usable line in the window if it settled at least once there. */
function hasPointsInWindow(s: FuturesHistorySeries, start: number, end: number): boolean {
  for (const p of s.points) {
    if (p.settlement === null) continue;
    const t = Date.parse(p.date);
    if (!Number.isFinite(t)) continue;
    if (t >= start && t <= end) return true;
  }
  return false;
}

/**
 * Colour for a contract: one hue family per tenor (months ≈ amber, quarters ≈
 * violet, years ≈ sky), with lightness stepped across the chronological index
 * so neighbouring contracts in the same group are distinguishable.
 */
function colorForTenor(tenor: ContractTenor, idx: number, total: number): string {
  const hue = tenor === "month" ? 35 : tenor === "quarter" ? 265 : 205;
  const t = total <= 1 ? 0.5 : idx / (total - 1);
  const light = 42 + t * 26; // 42%..68%
  return `hsl(${hue} 72% ${light.toFixed(1)}%)`;
}

function fmtTickDate(ms: number): string {
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    timeZone: "Europe/Berlin",
  });
}

// `RANGE_OPTIONS` re-exported for tests / external callers if needed.
export { RANGE_OPTIONS };
